import { type UserModel } from "@/dtos/user";
import {
  createContext,
  useContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useAuth } from "@/hooks/useAuth";

interface AuthContextType {
  user: UserModel | null;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function AuthProvider({ children }: PropsWithChildren) {
  const { user, isLoading, logout } = useAuth();

  const value = useMemo((): AuthContextType => {
    return {
      isLoading,
      user,
      logout,
    };
  }, [isLoading, logout, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within a AuthProvider");
  }
  return context;
}

export { AuthProvider, useAuthContext };
